/**
 * Builds a WebChat store whose only job is to *stop* dictate mode from
 * auto-submitting.
 *
 * Default WebChat dictate flow when the speech recognizer emits a final
 * result:
 *   1. dispatch { type: 'WEB_CHAT/SET_SEND_BOX',    payload: { text }}
 *   2. dispatch { type: 'WEB_CHAT/SUBMIT_SEND_BOX', payload: { method: 'speech' }}
 * Step 2 posts the message before the user is done talking. Dropping that
 * action leaves the transcript sitting in the send box; the user then hits
 * the send arrow (which reappears next to the mic once dictate stops)
 * whenever they're ready.
 *
 * Returns null if the CDN build doesn't expose createStore, so callers can
 * fall back to WebChat's default (auto-submit) behaviour.
 */
export function createManualSubmitWebChatStore() {
  const createStore = window.WebChat?.createStore;
  if (typeof createStore !== 'function') return null;

  // WebChat's DictateState enum: IDLE=0, WILL_START=1, STARTING=2, DICTATING=3, STOPPING=4
  const IDLE = 0;

  return createStore(
    {},
    ({ dispatch, getState }) => (next) => (action) => {
      if (
        action.type === 'WEB_CHAT/SUBMIT_SEND_BOX' &&
        (action.payload as { method?: string } | undefined)?.method === 'speech'
      ) {
        // WebChat stores the recognized transcript as `dictateInterims`
        // (a list of strings) until it's committed by SUBMIT_SEND_BOX.
        // Dropping SUBMIT_SEND_BOX leaves the text stranded there and the
        // send box empty (so the mic keeps being the only button in the
        // send box). Copy the interims into the send box ourselves and
        // reset the dictate machine so WebChat swaps back to the text +
        // send-arrow layout — the user then clicks the arrow to send.
        const state = getState() as { dictateInterims?: unknown };
        const rawInterims = state?.dictateInterims;
        const interims = Array.isArray(rawInterims)
          ? (rawInterims as unknown[]).filter((x): x is string => typeof x === 'string')
          : [];
        const text = interims.join(' ').trim();

        if (text) {
          dispatch({ type: 'WEB_CHAT/SET_SEND_BOX', payload: { text } });
        }
        dispatch({
          type: 'WEB_CHAT/SET_DICTATE_INTERIMS',
          payload: { dictateInterims: [] },
        });
        dispatch({
          type: 'WEB_CHAT/SET_DICTATE_STATE',
          payload: { dictateState: IDLE },
        });
        return undefined;
      }
      return next(action);
    }
  );
}
